/**
 * WorkstationConfiguratorPage — Feb 2026 Final System Pass
 *
 * Route: /workstations/:useCaseSlug
 *
 * Premium product page. Not a form. Not a configurator dashboard.
 *
 *   • Server-authoritative pricing (POST /api/orders re-resolves).
 *   • Hardware-class mapping preserved: business/content -> everyday,
 *     creative -> professional, architecture -> performance.
 *   • Architecture & 3D remains Windows-only (no picker rendered).
 *   • Uses shared useSalesTax() hook.
 *   • Recommended software has two visual states:
 *       A) Available from Ur Designs — power_slug resolves to active
 *          /api/software product. Renders as small product card with
 *          "VIEW SOFTWARE →" link.
 *       B) Recommended / compatible — no product record. Quieter card,
 *          no CTA.
 *   • Selected platform + selected add-ons persist to sessionStorage
 *     keyed by useCaseSlug so navigating to Software / Back / Forward
 *     / Checkout-and-Back restores the customer's configuration for
 *     the tab session. NEVER cleared on Continue.
 *   • No Contact & Delivery form on the product page. Full Name /
 *     Email / Phone / Notes are collected at /checkout. Delivery
 *     Address is collected at /checkout when the cart contains a
 *     workstation (see CheckoutPage.jsx).
 *   • Customer-facing add-on display renames "On-site Delivery &
 *     Setup" -> "On-site Installation" without touching the stored
 *     name/id.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronDown, Check, Pencil, Trash2 } from "lucide-react";
import * as Icons from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { api, formatAWG, usdApprox } from "@/lib/api";
import { useSalesTax } from "@/lib/tax";
import { usePageSeo } from "@/lib/usePageSeo";
import { useJsonLd, buildProductSchema } from "@/lib/seoSchema";
import { BrandedLoader } from "@/components/util/BrandedLoader";
import { workstationIconForSlug } from "@/lib/workstationIcons";
import {
  resolveSoftwareCommerceState,
  rememberOriginForSoftware,
  readWorkstationSessionIfDirty,
  removeAttachedSoftware,
  attachedSoftwareSubtotal,
  STATE as SW_STATE,
} from "@/lib/softwareCommerceState";

// Ready-to-work commercial promise. Same block on every workstation
// (hard-coded here because it's a brand-level guarantee, not per-use-case
// merchandising — moving it to CMS would let it drift across products).
//
// Feb 2026 — removed the false "1-hour personal handover" bullet. Ur
// Designs does not keep every workstation in inventory; time-bounded
// handover promises were commercially inaccurate. The equivalent
// per-use-case bullet "Personal setup & handover included" now lives
// in each `use_cases.features[]` list instead (see migration
// `workstations_ready_to_work_v1`).
const READY_TO_WORK = [
  "Workstation selected for your workflow",
  "Sourced by Ur Designs",
  "Configured and tested by Ur Designs in Aruba",
  "Up to 3 supported apps prepared",
  "Delivered directly to you",
];

// Customer-facing display-only rename for one add-on. Keeps the stored
// name/id/price authoritative on the backend; only the visible label
// swaps. Any add-on whose stored name starts with "On-site Delivery &
// Setup" renders as "On-site Installation".
const displayAddonName = (raw) => {
  const s = (raw || "").trim();
  if (/^on[-\s]site delivery( & setup)?/i.test(s)) return "On-site Installation";
  return raw;
};

const sessionKey = (slug) => `workstation:${slug}`;

export default function WorkstationConfiguratorPage() {
  const { useCaseSlug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { taxRate, taxLabel, compute: computeTax } = useSalesTax();

  const [useCase, setUseCase] = useState(null);

  usePageSeo({
    title: useCase ? `${useCase.display_name} Workstations — Ur Designs` : undefined,
    description: useCase?.short_description || undefined,
    path: useCase ? `/workstations/${useCase.slug}` : undefined,
    image: useCase?.image || undefined,
  });
  useJsonLd(buildProductSchema({
    name: useCase?.display_name,
    image: useCase?.image,
    brand: "Ur Designs Workstations",
    category: useCase?.display_name ? `Professional Workstation for ${useCase.display_name}` : undefined,
    description: useCase?.short_description,
  }));
  const [softwareIndex, setSoftwareIndex] = useState({}); // slug -> {name, tagline, vendor_url, status}
  const [fulfillmentEstimate, setFulfillmentEstimate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Hydrate saved config (platform + add-ons) BEFORE the platform
  // auto-pick effect runs, so a returning shopper sees their prior
  // selection restored instead of being reset to the auto-pick default.
  //
  // Feb 2026 — DIRTY-GATED RESTORATION. Only sessions marked
  // `dirty: true` (i.e. written after a real customer interaction —
  // platform click, add-on toggle, external-setup toggle, or
  // software attach/remove) are restored. Any legacy/test/pre-fix
  // payload lacking that marker is silently discarded so a fresh
  // configuration always begins with:
  //   platform:  ""        (until customer explicitly picks, unless
  //                         the workstation supports only one platform)
  //   add-ons:   []
  //   attached:  []
  //   ext-setup: {}
  const savedInitial = (() => {
    try { return readWorkstationSessionIfDirty(useCaseSlug); }
    catch { return null; }
  })();
  const [platform, setPlatform] = useState(savedInitial?.platform || "");
  const [selectedAddons, setSelectedAddons] = useState(
    Array.isArray(savedInitial?.selectedAddons) ? savedInitial.selectedAddons : []
  );
  // Attached software list — configured on the Software Detail page
  // and persisted here via sessionStorage. Each entry carries
  // { power_slug, software_id, name, publisher, sourcing, sales_mode,
  // state, unit_price, currency, quantity, selections[] }.
  const [attachedSoftware, setAttachedSoftware] = useState(
    Array.isArray(savedInitial?.attached_software) ? savedInitial.attached_software : []
  );
  // External-software "include setup support" toggles — keyed by
  // power_slug (or by name as fallback). Purely metadata; never
  // contributes to the payable subtotal.
  const [externalSetupWanted, setExternalSetupWanted] = useState(
    (savedInitial?.external_setup_wanted && typeof savedInitial.external_setup_wanted === "object")
      ? savedInitial.external_setup_wanted
      : {}
  );
  // Explicit-interaction gate. True only after the customer has taken
  // a real action on THIS workstation (platform click, add-on toggle,
  // external-setup toggle, or software attach/remove). The persist
  // effect below refuses to write anything to sessionStorage until
  // this flag is set, so mount alone can never manufacture a
  // configured workstation.
  const [interacted, setInteracted] = useState(!!savedInitial);
  const [specOpen, setSpecOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load use-case + software index + site settings in parallel.
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/use-cases/${useCaseSlug}`).then((r) => r.data).catch(() => null),
      api.get("/software").then((r) => r.data).catch(() => []),
      api.get("/settings").then((r) => r.data).catch(() => ({})),
    ]).then(([uc, sw, settings]) => {
      if (!uc) { setError("Workstation not found."); setLoading(false); return; }
      setUseCase(uc);
      const idx = {};
      for (const s of (sw || [])) {
        if (s?.slug) idx[s.slug] = {
          name: s.name || s.slug,
          tagline: s.tagline || s.description_short || "",
          vendor_url: s.vendor_url || "",
          publisher: s.publisher || "",
          status: s.status || "active",
          sales_mode: s.sales_mode || "request_quote",
          price: Number(s.price || 0),
          option_groups: Array.isArray(s.option_groups) ? s.option_groups : [],
        };
      }
      setSoftwareIndex(idx);
      setFulfillmentEstimate(
        settings?.workstations_fulfillment_estimate ||
        "Your workstation is sourced, configured and tested by Ur Designs, then delivered directly to you. Most individual workstations are ready in approximately 10 working days, depending on model availability and configuration."
      );
      // Auto-pick platform ONLY when the workstation supports
      // exactly one platform (product-level constraint, e.g.
      // Architecture & 3D is Windows-only). Multi-platform
      // workstations MUST start with no selection — the customer
      // must click Windows or Mac explicitly. This auto-pick does
      // NOT flip the `interacted` flag, so it never gets persisted
      // to sessionStorage and never masquerades as a saved customer
      // configuration on a future visit.
      if (!platform) {
        if (uc.windows_supported && !uc.mac_supported) setPlatform("windows");
        else if (!uc.windows_supported && uc.mac_supported) setPlatform("mac");
      }
    }).catch(() => setError("Workstation not found."))
      .finally(() => setLoading(false));
  }, [useCaseSlug]);

  // Persist platform + add-on + attached-software selections for this
  // tab session. GATED on `interacted` — writes only happen AFTER a
  // real customer action, so mount alone (and the single-platform
  // auto-pick above) can never seed a pseudo-configuration. Never
  // cleared on Continue so returning from Checkout via Back restores
  // the configuration.
  useEffect(() => {
    if (!useCaseSlug || !interacted) return;
    try {
      sessionStorage.setItem(
        sessionKey(useCaseSlug),
        JSON.stringify({
          platform,
          selectedAddons,
          attached_software: attachedSoftware,
          external_setup_wanted: externalSetupWanted,
          dirty: true,
        }),
      );
    } catch {}
  }, [useCaseSlug, platform, selectedAddons, attachedSoftware, externalSetupWanted, interacted]);

  // Sync attached_software back from sessionStorage whenever the tab
  // becomes visible again — this covers the case where the customer
  // configured software on the SoftwareDetailPage (which wrote to
  // sessionStorage directly via attachSoftwareToWorkstation and
  // marked the session dirty) then navigated back via router. The
  // savedInitial closure only ran once on mount, so we need a light
  // refresh on focus/visibility. Dirty-gated so non-dirty payloads
  // are ignored (and auto-purged by readWorkstationSessionIfDirty).
  useEffect(() => {
    const refresh = () => {
      const parsed = readWorkstationSessionIfDirty(useCaseSlug);
      if (!parsed) return;
      if (Array.isArray(parsed.attached_software)) {
        setAttachedSoftware(parsed.attached_software);
      }
      if (parsed.external_setup_wanted && typeof parsed.external_setup_wanted === "object") {
        setExternalSetupWanted(parsed.external_setup_wanted);
      }
      // A dirty session means the customer has interacted — arm the
      // persist gate so subsequent updates in this tab flow through.
      setInteracted(true);
    };
    refresh();
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [useCaseSlug]);

  // NOTE (Feb 2026): the previous mount effect that called
  // `rememberWorkstationOrigin(slug, name)` unconditionally on every
  // workstation visit has been removed. It was the source of the
  // stale-context bug where a subsequent Home → Software click would
  // wrongly show "Back to <last visited workstation>". Origin
  // markers are now written per-software at the exact moment of a
  // workstation → software CTA click below.

  const hc = useCase?.resolved_hardware_class;
  const plat = platform && hc ? (hc[platform] || {}) : null;
  const anchorPlat = plat || (hc ? ((hc.windows?.active && hc.windows) || (hc.mac?.active && hc.mac) || {}) : {});
  const basePrice = (plat?.price ?? anchorPlat?.price) || 0;
  const isPlaceholder = !!(plat?.is_placeholder ?? anchorPlat?.is_placeholder);
  const currentModel = (plat?.current_model || anchorPlat?.current_model || "");

  const addonSum = useMemo(
    () => (hc?.addons || [])
      .filter((a) => selectedAddons.includes(a.id))
      .reduce((s, a) => s + Number(a.price || 0), 0),
    [hc, selectedAddons]
  );
  // Attached software subtotal — ONLY PRICED items contribute. The
  // resolver enforces this even if a QUOTE_REQUIRED entry ever
  // carries a stray unit_price (defence-in-depth).
  const softwareSum = useMemo(
    () => (attachedSoftware || []).reduce((s, line) => s + attachedSoftwareSubtotal(line), 0),
    [attachedSoftware]
  );
  const subtotal = basePrice + addonSum + softwareSum;
  const { taxAmount, total: totalWithTax } = computeTax(subtotal);
  // Any QUOTE_REQUIRED attachment marks this workstation as needing
  // final pricing confirmation before payment. Surfaced in the Order
  // Summary and stored on cart-line meta for the checkout to display
  // and for the order record to preserve.
  const hasQuoteRequired = (attachedSoftware || []).some(
    (l) => l.state === SW_STATE.QUOTE_REQUIRED
  );

  const canContinue = !!(useCase && hc && platform && !submitting);

  // Explicit customer-action handlers. Each flips `interacted` so the
  // persist effect starts writing to sessionStorage — a fresh visit
  // that never triggers one of these will never leave a saved
  // configuration behind.
  const selectPlatform = (key) => {
    setPlatform(key);
    setInteracted(true);
  };

  const toggleAddon = (id) => {
    setSelectedAddons((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setInteracted(true);
  };

  const handleRemoveAttachedSoftware = (powerSlug) => {
    setAttachedSoftware((prev) => prev.filter((x) => x.power_slug !== powerSlug));
    removeAttachedSoftware(useCaseSlug, powerSlug);
    setInteracted(true);
  };

  const toggleExternalSetup = (rec) => {
    // Key by power_slug when available, otherwise by name (some
    // recommendation entries are catalog-less).
    const key = rec.power_slug || rec.name;
    if (!key) return;
    setExternalSetupWanted((prev) => ({ ...prev, [key]: !prev[key] }));
    setInteracted(true);
  };

  const handleContinue = () => {
    if (!canContinue) return;
    setSubmitting(true);
    try {
      // Build the external-setup-support list (only entries the
      // customer explicitly opted in on external software cards).
      const externalSetupSelections = Object.entries(externalSetupWanted)
        .filter(([, wanted]) => !!wanted)
        .map(([key]) => {
          const rec = (useCase.recommended_software || []).find(
            (r) => (r.power_slug || r.name) === key
          );
          return {
            power_slug: rec?.power_slug || "",
            name: rec?.name || key,
            publisher: rec?.name || "",
          };
        });
      addItem({
        kind: "laptop",
        line_type: "hardware_class",
        service_slug: useCase.slug,
        item_code: hc.slug,
        item_id: hc.id,
        quantity: 1,
        selected_addons: selectedAddons.map((id) => {
          const a = hc.addons.find((x) => x.id === id);
          return { item_id: id, item_code: "", name: a?.name };
        }).filter(Boolean),
        meta: {
          use_case_slug: useCase.slug,
          use_case_name: useCase.display_name,
          hardware_class_slug: hc.slug,
          hardware_class_name: hc.display_name,
          platform,
          current_model: currentModel,
          contact: {},
          recommended_software: useCase.recommended_software || [],
          // Unified commerce (Feb 2026) — attached software configured
          // on the SoftwareDetailPage in workstation context. Preserved
          // in order.line.meta for the admin dashboard. PRICED lines
          // contribute to subtotal via the workstation total; QUOTE_
          // REQUIRED lines never do. External setup-support opt-ins
          // are metadata only (no price, no line item).
          attached_software: attachedSoftware,
          external_setup_selections: externalSetupSelections,
          awaiting_pricing_confirmation: hasQuoteRequired,
        },
        display_name: `${useCase.display_name} — ${hc.display_name} (${platform === "windows" ? "Windows" : "Mac"})`,
        price: basePrice, // display only — server re-resolves
      });
      navigate("/checkout");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <BrandedLoader label="Loading workstation…" />;
  if (error || !useCase) {
    return (
      <div className="min-h-screen bg-black text-white grid place-items-center p-8">
        <div className="text-center max-w-md">
          <h1 className="font-display text-3xl font-bold">Workstation not found</h1>
          <p className="text-[#A3A3A3] mt-3">{error}</p>
          <Link to="/workstations" className="ur-btn-primary inline-flex mt-6">Back to Workstations</Link>
        </div>
      </div>
    );
  }

  const showPlatformPicker = useCase.windows_supported && useCase.mac_supported;
  const singlePlatform = useCase.windows_supported && !useCase.mac_supported ? "windows"
                     : (!useCase.windows_supported && useCase.mac_supported ? "mac" : "");

  return (
    <div data-testid="workstation-configurator" className="min-h-screen bg-black text-white">
      {/* ===== PRODUCT HERO ===== */}
      <section className="border-b border-[#171717]">
        <div className="max-w-[1240px] mx-auto px-4 sm:px-8 pt-8 pb-14">
          <Link
            to="/workstations"
            className="inline-flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-[#FF6600] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All Workstations
          </Link>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] gap-10 lg:gap-14 items-start">
            {/* LEFT — large product image */}
            <div className="rounded-2xl overflow-hidden border border-[#1F1F1F] bg-[#0A0A0A]">
              <div className="relative aspect-[16/10] w-full">
                {useCase.image ? (
                  <img
                    data-testid="workstation-hero-image"
                    src={useCase.image}
                    alt={useCase.display_name}
                    className="w-full h-full object-cover"
                  />
                ) : null}
                {(() => {
                  // Category icon badge — matches the top-left treatment
                  // used on Home + /workstations product cards. Uses the
                  // same centralized slug -> lucide-react mapping so all
                  // three surfaces stay synchronized.
                  const iconName = workstationIconForSlug(useCase.slug);
                  const Icon = iconName ? Icons[iconName] : null;
                  if (!Icon) return null;
                  return (
                    <div
                      data-testid="workstation-hero-icon-badge"
                      className="absolute top-4 left-4 sm:top-5 sm:left-5 w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-[#0F0F0F]/95 border border-[#FF6600]/40 grid place-items-center backdrop-blur-sm"
                    >
                      <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#FF6600]" strokeWidth={1.75} />
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* RIGHT — proposition + starting price */}
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#FF6600]">
                {useCase.display_name}
              </div>
              <h1
                data-testid="workstation-hc-name"
                className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mt-2 leading-[1.05]"
              >
                {useCase.display_name} Workstation
              </h1>
              {useCase.short_description && (
                <p className="mt-4 text-[14px] text-[#B5B5B5] leading-relaxed">
                  {useCase.short_description}
                </p>
              )}

              {/* Ready to work — value proposition */}
              <div className="mt-7">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">
                  Ready To Work From Day One
                </div>
                <ul data-testid="workstation-ready-to-work" className="mt-3 space-y-2">
                  {READY_TO_WORK.map((line, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-[14px] text-[#D1D1D1] leading-snug">
                      <Check className="w-4 h-4 text-[#FF6600] mt-0.5 shrink-0" strokeWidth={2.5} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 text-[11px] text-[#7A7A7A] leading-snug">
                  Software licenses are sold separately unless explicitly purchased.
                </div>

                {/* Online-store + delivery fulfillment block. Communicates
                    that Ur Designs runs the workstation business as an
                    online store — no physical retail location, no pickup.
                    Copy comes from Site Settings so the owner can adjust
                    the ~10-working-day expectation without a redeploy. */}
                <div
                  data-testid="workstation-fulfillment-block"
                  className="mt-5 rounded-xl border border-[#1F1F1F] bg-[#0A0A0A] p-4"
                >
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-white">
                    Ordered online. Configured by Ur Designs. Delivered to you.
                  </div>
                  <p
                    data-testid="workstation-fulfillment-estimate"
                    className="mt-2 text-[12px] text-[#A3A3A3] leading-relaxed"
                  >
                    {fulfillmentEstimate}
                  </p>
                </div>
              </div>

              {/* Starting price */}
              <div className="mt-8 flex items-baseline gap-3">
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Starting at</div>
                <div className="font-display text-3xl sm:text-4xl font-black text-white leading-none">
                  AWG {formatAWG(basePrice)}{isPlaceholder && <span data-testid="ws-price-asterisk" className="text-[#FF6600]">*</span>}
                </div>
              </div>
              {isPlaceholder && (
                <div className="mt-2 text-[11px] text-[#7A7A7A]"><span data-testid="ws-disclaimer-asterisk" className="text-[#FF6600]">*</span> Pricing currently under review.</div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== BODY + STICKY SUMMARY ===== */}
      <section className="max-w-[1240px] mx-auto px-4 sm:px-8 py-14 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10 sm:gap-14">
        {/* ===== LEFT COLUMN ===== */}
        <div className="min-w-0 space-y-14">

          {/* B — Platform */}
          {showPlatformPicker ? (
            <div data-testid="workstation-platform-step">
              <h2 className="font-display text-xl font-bold tracking-tight">Choose Your Platform</h2>
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {["windows", "mac"].map((key) => {
                  const p = hc[key] || {};
                  const disabled = !p.active;
                  const selected = platform === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      data-testid={`workstation-platform-${key}`}
                      onClick={() => !disabled && selectPlatform(key)}
                      disabled={disabled}
                      aria-pressed={selected}
                      className={`text-left rounded-xl border p-4 transition-all ${
                        disabled ? "border-[#171717] bg-[#0a0a0a] opacity-40 cursor-not-allowed" :
                        selected ? "border-[#FF6600] bg-[#141414]" :
                        "border-[#262626] bg-[#0a0a0a] hover:border-[#404040]"
                      }`}
                    >
                      <div className="text-sm font-bold uppercase tracking-widest">
                        {key === "windows" ? "Windows" : "Mac"}
                      </div>
                      {!disabled && (
                        <div className="text-[13px] text-[#D1D1D1] mt-1.5">AWG {formatAWG(p.price || 0)}</div>
                      )}
                      {p.badge && !disabled && (
                        <div className="text-[10px] text-[#FF6600] mt-1 font-semibold uppercase tracking-wider">{p.badge}</div>
                      )}
                      {disabled && p.subtext && (
                        <div className="text-[11px] text-[#A3A3A3] mt-2">{p.subtext}</div>
                      )}
                    </button>
                  );
                })}
              </div>
              {useCase.platform_caveat && (
                <div className="mt-3 text-[11px] text-[#F59E0B]">{useCase.platform_caveat}</div>
              )}
            </div>
          ) : (
            <div data-testid="workstation-platform-step-single" className="flex items-baseline gap-3">
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Platform</div>
              <div className="text-[15px] font-semibold text-white">
                {singlePlatform === "windows" ? "Windows" : "Mac"}
              </div>
            </div>
          )}

          {/* Technical specs collapsible + current model caveat */}
          {(hc.min_spec || []).length > 0 && (
            <div>
              <details
                open={specOpen}
                onToggle={(e) => setSpecOpen(e.currentTarget.open)}
                data-testid="workstation-tech-specs"
              >
                <summary className="list-none cursor-pointer inline-flex items-center gap-2 text-[12px] font-semibold text-[#A3A3A3] hover:text-white transition-colors">
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${specOpen ? "rotate-180" : ""}`} />
                  {specOpen ? "Hide Technical Specifications" : "View Technical Specifications"}
                </summary>
                <ul className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-[13px] text-[#B5B5B5]">
                  {hc.min_spec.map((line, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#5A5A5A] mt-0.5">•</span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                {currentModel && (
                  <div data-testid="workstation-current-model" className="mt-5 text-[12px] text-[#7A7A7A]">
                    <span className="text-[#A3A3A3] font-semibold">Currently supplied:</span> {currentModel}
                    <span className="block mt-1 text-[11px] leading-snug">
                      Model may vary based on availability. Any supplied model meets or exceeds the specification above.
                    </span>
                  </div>
                )}
              </details>
            </div>
          )}

          {/* C — Optional add-ons */}
          {(hc.addons || []).length > 0 && (
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight">Customize Your Workstation</h2>
              <p className="mt-1.5 text-[13px] text-[#A3A3A3]">Add only the extra services you need.</p>
              <div className="mt-4 divide-y divide-[#1F1F1F] border-y border-[#1F1F1F]">
                {hc.addons.map((a) => {
                  const checked = selectedAddons.includes(a.id);
                  const displayName = displayAddonName(a.name);
                  return (
                    <label
                      key={a.id}
                      data-testid={`workstation-addon-${a.id}`}
                      className={`flex items-center justify-between gap-3 py-3.5 cursor-pointer transition-colors ${checked ? "text-white" : "text-[#D1D1D1]"}`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAddon(a.id)}
                          className="mt-1 accent-[#FF6600]"
                          aria-label={displayName}
                        />
                        <div className="min-w-0">
                          <div className="text-[14px] font-semibold">{displayName}</div>
                          {a.description && <div className="text-[12px] text-[#7A7A7A] leading-snug">{a.description}</div>}
                        </div>
                      </div>
                      <div className="text-[14px] font-semibold shrink-0 tabular-nums">+ AWG {formatAWG(a.price)}</div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* D — Complete Your Workstation (recommended software) */}
          {(useCase.recommended_software || []).length > 0 && (
            <div data-testid="workstation-recommended-software">
              <h2 className="font-display text-xl font-bold tracking-tight">Complete Your Workstation</h2>
              <p className="mt-2 text-[13px] text-[#A3A3A3] max-w-2xl leading-relaxed">
                Software recommended for this workflow. Configure any Ur Designs software right here and it will be added to your workstation — no separate checkout, no re-entering your details.
              </p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {useCase.recommended_software.map((s, i) => {
                  const resolved = s.power_slug ? softwareIndex[s.power_slug] : null;
                  // Adapt the softwareIndex row (which is a light
                  // summary) into the shape the resolver expects.
                  const productLike = resolved
                    ? {
                        slug: s.power_slug,
                        name: resolved.name,
                        tagline: resolved.tagline,
                        publisher: resolved.publisher || "",
                        status: resolved.status || "active",
                        sales_mode: resolved.sales_mode || "request_quote",
                        price: Number(resolved.price || 0),
                        option_groups: resolved.option_groups || [],
                      }
                    : null;
                  const stateInfo = resolveSoftwareCommerceState(s, productLike);
                  const alreadyAttached = (attachedSoftware || []).some(
                    (x) => x.power_slug === s.power_slug
                  );
                  // Feb 2026 — RECOMMENDED SOFTWARE ≠ PRECONFIGURED
                  // SOFTWARE. The workstation recommendation card
                  // ALWAYS opens the software page with intent="attach"
                  // so no session data (including a legitimate prior
                  // attach) can pre-select for the customer. The ONLY
                  // path that restores prior selections is the
                  // explicit "Change" button on the attached-software
                  // row rendered further down the page.
                  const workstationOrigin = {
                    fromWorkstationSlug: useCase.slug,
                    fromWorkstationName: useCase.display_name,
                    intent: "attach",
                  };

                  // ─── EXTERNAL (customer buys from provider) ────
                  if (stateInfo.state === SW_STATE.EXTERNAL) {
                    const key = s.power_slug || s.name;
                    const wantsSetup = !!externalSetupWanted[key];
                    const canConfigure = s.setup_supported !== false;
                    return (
                      <div
                        key={i}
                        data-testid={`workstation-software-external-${i}`}
                        className="rounded-xl border border-[#1A1A1A] bg-[#080808] p-4 flex flex-col"
                      >
                        <div className="font-semibold text-[14px] text-white">{s.name}</div>
                        {(s.note || resolved?.tagline) && (
                          <div className="text-[12px] text-[#A3A3A3] mt-0.5 leading-snug">
                            {s.note || resolved?.tagline}
                          </div>
                        )}
                        <div className="mt-3 text-[12px] text-[#B5B5B5] leading-relaxed">
                          Subscribe directly with{" "}
                          <span className="font-semibold text-white">
                            {stateInfo.publisher || "the provider"}
                          </span>
                          .
                          {canConfigure && " We can configure it as part of your workstation setup."}
                        </div>
                        {canConfigure && (
                          <label
                            data-testid={`workstation-software-external-setup-${i}`}
                            className="mt-3 inline-flex items-start gap-2 cursor-pointer text-[12px] text-[#D1D1D1]"
                          >
                            <input
                              type="checkbox"
                              checked={wantsSetup}
                              onChange={() => toggleExternalSetup(s)}
                              className="mt-0.5 accent-[#FF6600]"
                            />
                            <span>Include setup support during my workstation setup</span>
                          </label>
                        )}
                      </div>
                    );
                  }

                  // ─── PRICED or QUOTE_REQUIRED (Ur Designs sells) ───
                  const priceLabel =
                    stateInfo.state === SW_STATE.PRICED
                      ? `AWG ${formatAWG(Number(productLike?.price || 0))}`
                      : "Price confirmed at checkout";
                  const ctaLabel = stateInfo.configurable
                    ? (alreadyAttached ? "Change Options" : "Choose Options")
                    : (alreadyAttached ? "Attached" : "Add to Workstation");
                  return (
                    <div
                      key={i}
                      data-testid={`workstation-software-available-${i}`}
                      className={`rounded-xl border p-4 flex flex-col ${
                        alreadyAttached
                          ? "border-[#FF6600]/60 bg-[#141010]"
                          : "border-[#262626] bg-[#0a0a0a]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-[14px] text-white">{s.name}</div>
                          {(resolved?.tagline || s.note) && (
                            <div className="text-[12px] text-[#A3A3A3] mt-0.5 leading-snug">
                              {resolved?.tagline || s.note}
                            </div>
                          )}
                        </div>
                        {alreadyAttached && (
                          <span className="inline-flex px-2 py-0.5 rounded-full bg-[#FF6600] text-black text-[10px] font-bold uppercase tracking-wider shrink-0">
                            Attached
                          </span>
                        )}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        <span className="inline-flex px-2 py-0.5 rounded-full bg-[#FF6600]/12 border border-[#FF6600]/40 text-[#FF6600] text-[10px] font-bold uppercase tracking-wider">
                          Available from Ur Designs
                        </span>
                        <span className="inline-flex px-2 py-0.5 rounded-full border border-[#262626] text-[#A3A3A3] text-[10px] font-semibold uppercase tracking-wider">
                          {priceLabel}
                        </span>
                      </div>
                      {stateInfo.linkable && (
                        <Link
                          to={`/software/${s.power_slug}`}
                          state={workstationOrigin}
                          onClick={() => rememberOriginForSoftware(s.power_slug, useCase.slug, useCase.display_name, "attach")}
                          data-testid={`workstation-software-cta-${i}`}
                          className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold text-white hover:text-[#FF6600] transition-colors uppercase tracking-widest"
                        >
                          {ctaLabel} <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
              <Link
                to="/software"
                data-testid="workstation-explore-software"
                className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#A3A3A3] hover:text-[#FF6600] transition-colors"
              >
                Explore All Software <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          )}

          {/* D2 — Attached Software (customer's configured selections) */}
          {attachedSoftware.length > 0 && (
            <div data-testid="workstation-attached-software">
              <h2 className="font-display text-xl font-bold tracking-tight">Your Software Selections</h2>
              <p className="mt-1.5 text-[13px] text-[#A3A3A3]">
                These will be added to your unified workstation order.
              </p>
              <div className="mt-4 divide-y divide-[#1F1F1F] border-y border-[#1F1F1F]">
                {attachedSoftware.map((line) => {
                  const isPriced = line.state === SW_STATE.PRICED;
                  const selectionSummary = (line.selections || [])
                    .filter((x) => x.option_label)
                    .map((x) => x.option_label)
                    .join(" · ");
                  return (
                    <div
                      key={line.power_slug}
                      data-testid={`workstation-attached-${line.power_slug}`}
                      className="py-3.5 flex items-start justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-white">{line.name}</div>
                        {selectionSummary && (
                          <div className="text-[12px] text-[#A3A3A3] leading-snug">{selectionSummary}</div>
                        )}
                        <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-[#FF6600]">
                          {isPriced ? "Priced" : "Price to be confirmed"}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-right">
                          <div className="text-[13px] font-semibold text-white tabular-nums">
                            {isPriced
                              ? `AWG ${formatAWG(attachedSoftwareSubtotal(line))}`
                              : "—"}
                          </div>
                        </div>
                        <Link
                          to={`/software/${line.power_slug}`}
                          state={{
                            fromWorkstationSlug: useCase.slug,
                            fromWorkstationName: useCase.display_name,
                            intent: "edit",
                          }}
                          onClick={() => rememberOriginForSoftware(line.power_slug, useCase.slug, useCase.display_name, "edit")}
                          data-testid={`workstation-attached-change-${line.power_slug}`}
                          className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#A3A3A3] hover:text-[#FF6600]"
                          title="Change options"
                        >
                          <Pencil className="w-3 h-3" /> Change
                        </Link>
                        <button
                          type="button"
                          data-testid={`workstation-attached-remove-${line.power_slug}`}
                          onClick={() => handleRemoveAttachedSoftware(line.power_slug)}
                          className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#A3A3A3] hover:text-[#F87171]"
                          title="Remove"
                        >
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ===== ORDER SUMMARY ===== */}
        <aside className="lg:sticky lg:top-24 self-start rounded-2xl border border-[#262626] bg-[#0a0a0a] p-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">Order Summary</div>
          <div className="mt-3">
            <div className="text-[13px] text-[#A3A3A3]">{useCase.display_name}</div>
            <div className="font-display text-lg font-bold leading-tight mt-0.5">{useCase.display_name} Workstation</div>
          </div>

          {showPlatformPicker ? (
            <div
              data-testid="workstation-summary-platform-row"
              className="mt-4 flex items-center justify-between text-[13px]"
            >
              <span className="text-[#A3A3A3]">Platform</span>
              {platform ? (
                <span
                  data-testid="workstation-summary-platform-value"
                  className="text-white font-semibold"
                >
                  {platform === "windows" ? "Windows" : "Mac"}
                </span>
              ) : (
                <span
                  data-testid="workstation-summary-platform-unselected"
                  className="text-[#7A7A7A] italic"
                >
                  Select a platform
                </span>
              )}
            </div>
          ) : (
            platform && (
              <div className="mt-4 flex items-center justify-between text-[13px]">
                <span className="text-[#A3A3A3]">Platform</span>
                <span className="text-white font-semibold">{platform === "windows" ? "Windows" : "Mac"}</span>
              </div>
            )
          )}

          <div className="mt-4 pt-4 border-t border-[#1F1F1F] space-y-1.5 text-[13px]">
            <div className="flex justify-between">
              <span className="text-[#A3A3A3]">Workstation</span>
              <span className="text-white tabular-nums">AWG {formatAWG(basePrice)}</span>
            </div>
            {selectedAddons.length > 0 && (hc.addons || [])
              .filter(a => selectedAddons.includes(a.id))
              .map((a) => (
                <div key={a.id} className="flex justify-between text-[12px]">
                  <span className="text-[#A3A3A3]">+ {displayAddonName(a.name)}</span>
                  <span className="text-[#D1D1D1] tabular-nums">AWG {formatAWG(a.price)}</span>
                </div>
              ))
            }
            {attachedSoftware.length > 0 && (
              <div className="pt-2 mt-2 border-t border-[#1F1F1F] space-y-1.5">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#A3A3A3]">Software</div>
                {attachedSoftware.map((line) => {
                  const isPriced = line.state === SW_STATE.PRICED;
                  const selSummary = (line.selections || [])
                    .filter((x) => x.option_label)
                    .map((x) => x.option_label)
                    .join(" · ");
                  return (
                    <div
                      key={line.power_slug}
                      data-testid={`workstation-summary-software-${line.power_slug}`}
                      className="flex justify-between text-[12px]"
                    >
                      <span className="text-[#A3A3A3] min-w-0 truncate mr-2">
                        + {line.name}
                        {selSummary && (
                          <span className="text-[#7A7A7A]"> · {selSummary}</span>
                        )}
                      </span>
                      <span className={`tabular-nums shrink-0 ${isPriced ? "text-[#D1D1D1]" : "text-[#7A7A7A]"}`}>
                        {isPriced ? `AWG ${formatAWG(attachedSoftwareSubtotal(line))}` : "TBD"}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-[#1F1F1F] space-y-1.5 text-[13px]">
            <div className="flex justify-between">
              <span className="text-[#A3A3A3]">Subtotal</span>
              <span data-testid="workstation-subtotal" className="font-semibold text-white tabular-nums">AWG {formatAWG(subtotal)}</span>
            </div>
            {taxRate > 0 && (
              <div className="flex justify-between">
                <span className="text-[#A3A3A3]" data-testid="workstation-tax-label">{taxLabel}</span>
                <span data-testid="workstation-tax-amount" className="font-semibold text-white tabular-nums">AWG {formatAWG(taxAmount)}</span>
              </div>
            )}
            <div className="pt-2 flex items-end justify-between">
              <span className="text-white font-semibold text-[14px]">Estimated Total</span>
              <div className="text-right">
                <div data-testid="workstation-total" className="font-display text-2xl font-black leading-none">
                  AWG {formatAWG(totalWithTax)}
                </div>
                <div className="text-[11px] text-[#A3A3A3] mt-1">{usdApprox(totalWithTax)}</div>
              </div>
            </div>
            {isPlaceholder && (
              <div className="text-[10px] text-[#7A7A7A] pt-1">Final commercial pricing pending.</div>
            )}
            {hasQuoteRequired && (
              <div
                data-testid="workstation-quote-required-notice"
                className="text-[11px] text-[#F59E0B] pt-2 leading-snug"
              >
                One or more software items require final pricing confirmation. You'll complete the order after Ur Designs confirms the final price — no payment is taken yet for those items.
              </div>
            )}
          </div>

          <button
            data-testid="workstation-submit"
            onClick={handleContinue}
            disabled={!canContinue}
            className="ur-btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "…" : "Continue"} <ArrowRight className="w-4 h-4" />
          </button>
          <div className="mt-3 text-[11px] text-[#7A7A7A] leading-snug">
            You'll enter your delivery details on the next step.
          </div>
        </aside>
      </section>
    </div>
  );
}
